<template>
  <div
    class="colour-swatches"
    :class="{
      [$class('loading')]: productsByTagState === 'loading'
    }"
  >
    <div class="colour-swatches__header">
      <p
        class="colour-swatches__title text-body-2xs"
        v-text="$string('product.content.colour_swatches')"
      />

      <p
        class="colour-swatches__subtitle text-body-2xs"
        v-text="currentSwatchName"
      />
    </div>

    <div
      v-if="productsByTagState === 'ready'"
      class="colour-swatches__content"
    >
      <template
        v-for="(swatch, index) in swatches"
        :key="index"
      >
        <div
          v-if="swatch.available"
          class="colour-swatches__swatch"
          :class="{
            [$class('active')] : swatch.id === product.id,
            'colour-swatches__swatch--white': getSwatchColour(swatch) === '#ffffff'
          }"
          @click="updateProductId(swatch)"
        >
          <div
            class="colour-swatches__swatch-inner"
            :style="`background-color: ${getSwatchColour(swatch)}`"
          />
        </div>
      </template>
    </div>

    <div
      v-else
      class="colour-swatches__content"
    >
      <div
        v-for="swatch in familySize"
        :key="swatch"
        class="colour-swatches__swatch"
      >
        <div
          class="colour-swatches__swatch-inner"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Colour swatches (colour-swatches)
 * -----------------------------------------------------------------------------
 * Displays swatches for related product colourways, that link to that product.
 *
 * @param {Number, String} colourFamilySize - Number of colourways for the product.
 * @param {String} currentSwatchName - String name of the current colourway.
 * @param {Object} product - Product Object.
 *
 */
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'ColourSwatches',

  props: {
    colourFamilySize: {
      type: [Number, String],
      default: 6,
    },
    currentSwatchName:{
      type: [Boolean, String],
      default: '',
    },
    namespace: {
      type: String,
      default: 'product',
    },
    product: {
      type: [Object, Boolean],
      default: false,
    },
    stateProductsNamespace: {
      type: String,
      default: 'product',
    },
  },

  emits: ['product-id-update', 'update-variant'],

  data() {
    return {
      swatches: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      currentSize: (state) => state.product.size,
      colourFamily: (state) => state.product.colourFamily,
      productsByTagState: (state) => state.product.productsByTagState,
      sizeFamily: (state) => state.product.sizeFamily,
    }),

    ...mapGetters({
      getProductsByNamespace: 'product/getProductsByNamespace',
    }),

    products() {
      return this.getProductsByNamespace(this.namespace)
    },

    /**
     * Returns the current products swatch name.
     * @returns {String}
     */
    familySize() {
      const size = this.product?.metafields?.find(
        field => field.name === 'colour_family_name',
      )

      if (size?.value) {
        return parseInt(size.value)
      }

      if (typeof(this.colourFamilySize) === 'string') {
        return parseInt(this.colourFamilySize)
      }

      return this.colourFamilySize
    },
  },

  watch: {

    /**
     * Watch the products array and reconstruct the swatches when it updates.
     */
    products: {
      deep: true,
      eager: true,
      handler(val) {
        if (!val?.length) {
          return
        }

        this.constructSwatches()
      },
    },
  },

  methods: {

    /**
     * Updates the product id.
     * @param {Object} - Product object.
     */
    updateProductId(value) {
      if (!value) {
        return
      }

      const product = { value }

      if (product.value.handle === cnvs.page.handle) {
        return
      }

      this.$emit('update-variant', false)
      this.$emit('product-id-update', product)
    },

    /**
     * Helper function that returns the colour code.
     * @param {Object} swatch - Product object.
     * @returns {String}
     */
    getSwatchColour(swatch) {
      const colour = swatch.metafields.find(field => field.name === 'swatch_code')

      return colour ? colour.value : ''
    },

    /**
     * Handles coordinating the construction of swatches.
     */
    constructSwatches() {
      const sizeProducts = this.filterProductsBySize(this.products)
      const colourProducts = this.filterProductsByColour(sizeProducts)

      this.swatches = colourProducts
    },

    /**
     * Filters the products based on the current product size.
     * @param {Array} products - Array of products.
     * @returns {Array}
     */
    filterProductsBySize(products) {
      const size = `ProductSize-${this.currentSize}`

      return products.filter(product => {
        if (product.tags.find(tag => tag === size)) {
          return product
        }
      })
    },

    /**
     * Filters the products by current product colour family.
     * @param {Array} products - Array of products.
     * @returns {Array}
     */
    filterProductsByColour(products) {
      return products.filter(product => {
        if (
          product?.tags.find(tag => tag === this.colourFamily) &&
          product.metafields?.find(field => field.name === 'swatch_code')
        ) {
          return product
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './colour-swatches';
</style>
